import {
  ChangeDetectorRef,
  DestroyRef,
  Directive,
  HostListener,
  input,
  NgZone,
  OnInit,
  inject,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { DEFAULT_INTERRUPTSOURCES, Idle } from "@ng-idle/core";

import { CountdownDialog } from "components/dialogs/CountdownDialog/CountdownDialog.component";
import Organization from "models/Organization";
import { AuthFacade } from "state/auth.facade";
import { OrgFacade } from "state/org.facade";

@Directive({
  selector: "app-logout-idle",
  standalone: true,
})
export class LogoutIdleDirective implements OnInit {
  private readonly authFacade = inject(AuthFacade);
  private readonly orgFacade = inject(OrgFacade);
  private readonly dialog = inject(MatDialog);
  private readonly idle = inject(Idle);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  public readonly isActive = input<boolean>(true);

  private idleState = "NOT_STARTED";
  private countdown?: number = null;
  private isCountdownOpen = false;
  private dialogRef: MatDialogRef<CountdownDialog, any>;

  private org = toSignal<Organization | null>(this.orgFacade.org$);

  public ngOnInit(): void {
    this.subscribeToIdleEvents();

    // Wait for org to be defined before setting idle timeouts
    this.orgFacade.isOrgDefined$.subscribe(() => {
      this.setIdleTimeouts();
      this.reset();
    });
  }

  private subscribeToIdleEvents(): void {
    // upon idle
    this.idle.onIdleStart.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.idleState = "IDLE";
    });

    // no longer idle
    this.idle.onIdleEnd.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.handleIdleEnd();
    });

    // upon timeout, logout
    this.idle.onTimeout.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.handleTimeout();
    });

    // timeout warning / countdown
    this.idle.onTimeoutWarning
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((seconds) => {
        this.openCountdownDialog(seconds);
      });
  }

  private setIdleTimeouts(): void {
    const minsUntilLoggedOut = this.org()?.inactivityTimer ?? 15;
    const minsDialogIsDisplayedFor = 2;
    this.idle.setIdle((minsUntilLoggedOut - minsDialogIsDisplayedFor) * 60); // considered idle after default 13 minutes
    this.idle.setTimeout(minsDialogIsDisplayedFor * 60); // considered timed out after x seconds ( default 15 min, count down gets shown for 2 min.)
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES); // interrupt source (use default)
  }

  private reset(): void {
    this.idle.watch();
    this.isCountdownOpen = false;
    this.idleState = "NOT_IDLE";
    this.countdown = null;
  }

  /** Sync the idle state and countdown across tabs */
  @HostListener("window:storage", ["$event"])
  public syncIdleStateAcrossTabs(event: StorageEvent): void {
    if (!this.isActive()) return;
    if (event.key === "idleState") {
      const state = JSON.parse(event.newValue);
      this.idleState = state.idleState;
      this.countdown = state.countdown;
      switch (this.idleState) {
        case "IDLE":
          if (this.countdown !== null) {
            this.openCountdownDialog(this.countdown);
          }
          break;
        case "NOT_IDLE":
          this.handleIdleEnd();
          break;
        case "TIMED_OUT":
          this.handleTimeout();
          break;
        default:
          console.warn("Unhandled idle state:", this.idleState);
      }
    }
  }

  /** Open countdown dialog when idle warning is shown */
  private openCountdownDialog(seconds: number): void {
    if (!this.isActive()) return;
    this.countdown = seconds;
    if (!this.isCountdownOpen) {
      this.isCountdownOpen = true;
      this.dialogRef = this.dialog.open(CountdownDialog, {
        data: { countdown: seconds },
      });
    } else {
      this.dialogRef.componentInstance.countdownDialogData = { countdown: seconds };
    }
  }

  /** Handle when user is no longer idle */
  private handleIdleEnd(): void {
    this.ngZone.run(() => {
      this.dialog.closeAll();
    });
    this.isCountdownOpen = false;
    this.countdown = null;
    this.cd.detectChanges();
  }

  /** Handle when the idle timeout occurs */
  private handleTimeout(): void {
    if (!this.isActive()) return;
    this.broadcastIdleState("TIMED_OUT", null); // Broadcast timeout state across all tabs
    this.performLogout(); // Logout in the current tab
  }

  /** Logout action that is synchronized across all tabs */
  private performLogout(): void {
    this.authFacade.logout(true); // after login, return to current URL
    this.reset();
  }

  /** Broadcast idle state changes to other tabs */
  private broadcastIdleState(state: string, countdown: number | null): void {
    const idleState = JSON.stringify({ idleState: state, countdown });
    localStorage.setItem("idleState", idleState);
  }
}
